import { useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { Flex, Text, Modal, Alert, Spin, Button, message } from '@nula/core-ui';
import {
  useSubscription,
  useCreditBalance,
  useCreateCheckoutSession,
  useCreatePortalSession,
  useResumeSubscription,
  useChangePlan,
  useInvalidateBilling,
  useAwaitPlanActivation,
} from '@/lib/billing/use-subscription';
import { BillingStatusCard } from '@/lib/billing/billing-status-card';
import { PlanCatalog } from '@/lib/billing/plan-catalog';
import { ChangePlanConfirm } from '@/lib/billing/change-plan-confirm';
import { StripeElementsProvider } from '@/lib/billing/stripe-provider';
import { SubscribePanel } from '@/lib/billing/subscribe-panel';
import { CancelRetentionModal } from '@/lib/billing/cancel-retention-modal';
import { checkoutRejectionFromError } from '@/lib/billing/checkout-rejection';
import { hasSavedCard } from '@/lib/billing/plan-cta';
import type { CheckoutRejection } from '@/lib/billing/promo-code-input';
import type { BillingInterval, PlanCatalogEntry } from '@/lib/billing/types';
import { openExternalUrl } from '@/utils/open-external-url';
import styles from './subscription-tab.module.css';

export function SubscriptionTab() {
  const { data: subscription, isLoading } = useSubscription();
  const { data: credits } = useCreditBalance();
  const checkout = useCreateCheckoutSession();
  const portal = useCreatePortalSession();
  const resume = useResumeSubscription();
  const changePlan = useChangePlan();
  const invalidateBilling = useInvalidateBilling();
  const activation = useAwaitPlanActivation();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [changingPlan, setChangingPlan] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [pendingSwitch, setPendingSwitch] = useState<{
    plan: PlanCatalogEntry;
    interval: BillingInterval;
  } | null>(null);
  const [checkoutRejection, setCheckoutRejection] = useState<CheckoutRejection | null>(null);
  const rejectionNonce = useRef(0);

  const onFreeTier =
    !!subscription &&
    subscription.isSelfServe &&
    (subscription.planCode === 'basic' || subscription.status === 'canceled');
  // invoice.paid, not checkout, binds the row — until it lands the server still
  // reports the old plan, so the catalog would contradict a payment that worked.
  // A timed-out poll is a dead end (nothing keeps refetching), so unlike the
  // catalog, the status card stays visible underneath the notice — otherwise a
  // paid customer loses every billing control until a full reload.
  const awaitingActivation = activation.state === 'pending' || activation.state === 'timed_out';
  const showCatalog = (onFreeTier || changingPlan) && !awaitingActivation;

  // No live paid Stripe sub → first-time checkout. A trial is unbound only while
  // it has no card; once one is saved checkout answers 409 already_subscribed,
  // so that user changes plan instead.
  const noLiveSubscription =
    !!subscription &&
    (subscription.planCode === 'basic' ||
      (subscription.status === 'trialing' && !hasSavedCard(subscription)) ||
      subscription.status === 'canceled');

  const handleSelectPlan = async (plan: PlanCatalogEntry, interval: BillingInterval, promotionCode?: string) => {
    if (noLiveSubscription && plan.code !== 'basic') {
      try {
        const res = await checkout.mutateAsync({ planCode: plan.code, interval, promotionCode });
        setChangingPlan(false);
        setPendingSwitch(null);
        setClientSecret(res.clientSecret);
      } catch (err) {
        Sentry.captureException(err);
        rejectionNonce.current += 1;
        const rejection = checkoutRejectionFromError(err, rejectionNonce.current);
        if (rejection) {
          setCheckoutRejection(rejection);
        } else {
          message.error("Couldn't start checkout. Please try again.");
        }
      }
      return;
    }
    if (plan.code === 'basic') {
      await commitPlanChange(plan, interval);
      return;
    }
    setPendingSwitch({ plan, interval });
  };

  const commitPlanChange = async (plan: PlanCatalogEntry, interval: BillingInterval) => {
    try {
      await changePlan.mutateAsync({ planCode: plan.code, interval });
      setPendingSwitch(null);
      setChangingPlan(false);
      await invalidateBilling();
      message.success(plan.code === 'basic' ? 'Your plan will change to Basic.' : `Switched to ${plan.name}.`);
    } catch (err) {
      Sentry.captureException(err);
      message.error("Couldn't change your plan. Please try again.");
    }
  };

  const handlePortal = async () => {
    try {
      const res = await portal.mutateAsync();
      await openExternalUrl(res.url);
    } catch (err) {
      Sentry.captureException(err);
      message.error("Couldn't open billing. Please try again.");
    }
  };

  const handleResume = async () => {
    try {
      await resume.mutateAsync();
      message.success('Your plan will continue.');
    } catch (err) {
      Sentry.captureException(err);
      message.error("Couldn't resume your plan. Please try again.");
    }
  };

  const handleCheckActivationAgain = async () => {
    await invalidateBilling();
    activation.reset();
  };

  return (
    <Flex vertical gap="lg">
      {!changingPlan && (
        <Flex vertical gap="xs">
          <Text size="md" className={styles.tabTitle}>
            Subscription
          </Text>
          <Text size="sm" type="secondary">
            Manage your workspace billing and plan details.
          </Text>
        </Flex>
      )}

      {activation.state === 'pending' && (
        <Flex gap="sm" align="center" data-testid="activation-pending">
          <Spin size="sm" />
          <Text size="sm">Confirming your payment. This usually takes a few seconds.</Text>
        </Flex>
      )}

      {activation.state === 'timed_out' && (
        <Alert
          type="info"
          showIcon
          data-testid="activation-timed-out"
          message="Your payment is still processing"
          description="Your card was submitted successfully. Your new plan will appear here as soon as it's confirmed — there's no need to pay again. Contact support if it hasn't updated in a few minutes."
          action={
            <Button variant="text" size="sm" onClick={() => void handleCheckActivationAgain()}>
              Check again
            </Button>
          }
        />
      )}

      {activation.state === 'pending' ? null : subscription && showCatalog ? (
        <PlanCatalog
          subscription={subscription}
          onSelectPlan={(plan, interval, promotionCode) => void handleSelectPlan(plan, interval, promotionCode)}
          onBack={changingPlan ? () => setChangingPlan(false) : undefined}
          checkoutRejection={checkoutRejection}
        />
      ) : (
        <BillingStatusCard
          subscription={subscription}
          credits={credits}
          loading={isLoading}
          onSubscribe={() => setChangingPlan(true)}
          onChangePlan={() => setChangingPlan(true)}
          onManageBilling={() => void handlePortal()}
          onCancel={() => setCancelOpen(true)}
          onResume={() => void handleResume()}
        />
      )}

      <Modal
        open={clientSecret !== null}
        onClose={() => setClientSecret(null)}
        title="Upgrade to Nula Pro"
        footer={null}
        centered
      >
        {clientSecret && (
          <StripeElementsProvider clientSecret={clientSecret}>
            <SubscribePanel
              clientSecret={clientSecret}
              onSuccess={() => {
                setClientSecret(null);
                setChangingPlan(false);
                message.success('Payment received. Confirming your plan…');
                if (subscription) {
                  activation.start({
                    planCode: subscription.planCode,
                    interval: subscription.interval,
                    status: subscription.status,
                  });
                }
              }}
            />
          </StripeElementsProvider>
        )}
      </Modal>

      <CancelRetentionModal
        open={cancelOpen}
        workspaceType={subscription?.workspaceType}
        planName={subscription?.planName ?? 'Pro'}
        onClose={() => setCancelOpen(false)}
        onCanceled={(effectiveAt) => {
          setCancelOpen(false);
          message.info(
            effectiveAt
              ? `Your plan will end on ${new Date(effectiveAt).toLocaleDateString()}.`
              : 'Your plan will end at the end of the current billing period.',
          );
        }}
      />

      {pendingSwitch && (
        <ChangePlanConfirm
          open
          planCode={pendingSwitch.plan.code}
          planName={pendingSwitch.plan.name}
          interval={pendingSwitch.interval}
          onClose={() => setPendingSwitch(null)}
          onConfirm={() => commitPlanChange(pendingSwitch.plan, pendingSwitch.interval)}
        />
      )}
    </Flex>
  );
}
